import { AnyAction } from "redux";

// Pre-defined step sequence (must match backend STEP_SEQUENCE in matter_processor.py)
const MATTER_STEP_SEQUENCE = [
  "Collecting Context",
  "Organizing Data",
  "Analysing Legal Sources",
  "Generating Relevant Tasks",
  "Final Matter Evaluation",
];

// Create initial steps with Pending status
const createInitialSteps = () => MATTER_STEP_SEQUENCE.map(step => ({
  step,
  status: "Pending" as const,
  messages: [],
  elapsedSeconds: 0,
  iconIdentifier: undefined
}));

interface MatterState {
  matters: any[];
  assignedMatters: any[];
  currentMatter: any | null;
  isLoadingCurrentMatter: boolean;
  prefetchedMatterData: any | null;
  prefetchedMatterTasks: any[];
  isPreparingDoc: boolean;
  isConnecting: boolean;
  matterId?: string;
  correlationId?: string;
  progress: any[];
  progressPercent: number;
  isRunning: boolean;
  isCompleted: boolean;
}

const initialState: MatterState = {
  matters: [],
  assignedMatters: [],
  currentMatter: null,
  isLoadingCurrentMatter: false,
  prefetchedMatterData: null,
  prefetchedMatterTasks: [],
  isPreparingDoc: false,
  isConnecting: false,
  matterId: undefined,
  correlationId: undefined,
  progress: [],
  progressPercent: 0,
  isRunning: false,
  isCompleted: false
};

const merge_step = (arr: any[], patch: any) => {
  const idx = arr.findIndex((s: any) => s.step === patch.step);
  if (idx > -1) {
    const merged = {
      ...arr[idx],
      status: patch.status,
      messages: [...(arr[idx].messages || []), ...(patch.messages || [])],
      elapsedSeconds: patch.elapsedSeconds !== undefined ? patch.elapsedSeconds : arr[idx].elapsedSeconds,
      iconIdentifier: patch.iconIdentifier || arr[idx].iconIdentifier,
    };
    return [...arr.slice(0, idx), merged, ...arr.slice(idx + 1)];
  }
  return [...arr, patch];
};

export const matterReducer = (state = initialState, action: AnyAction): MatterState => {
  switch (action.type) {
    case 'FETCH_ALL_MATTERS':
      return { ...state, matters: action.payload };
    case 'APPEND_MATTERS':
      return { ...state, matters: [...state.matters, ...action.payload] };
    case 'FETCH_ASSIGNED_MATTERS':
      return { ...state, assignedMatters: action.payload };
    case 'PREPARING_MATTER_DOCUMENT':
      return {
        ...state,
        isPreparingDoc: action.payload.isPreparingDoc,
        progress: createInitialSteps(),
        progressPercent: 0,
        isCompleted: false
      };
    case 'CONNECTING_MATTER_CREATION_SERVICE':
      return {
        ...state,
        isConnecting: action.payload.isConnecting,
        matterId: action.payload.matter_id || state.matterId,
        correlationId: action.payload.correlation_id,
        isRunning: true,
        progress: state.progress.length > 0 ? state.progress : createInitialSteps(),
        progressPercent: 0,
        isCompleted: false
      };
    case 'MATTER_CREATION_CONNECTING':
      return {
        ...state,
        isConnecting: true,
        isPreparingDoc: false,
        progress: state.progress.length > 0 ? state.progress : createInitialSteps(),
        progressPercent: 0,
        isCompleted: false
      };
    case 'MATTER_CREATION_CONNECTED':
      return {
        ...state,
        isRunning: true,
        isConnecting: false,
        isPreparingDoc: false,
        progress: state.progress.length > 0 ? state.progress : createInitialSteps()
      };
    case 'MATTER_CREATION_PROGRESS_INIT':
      return {
        ...state,
        isRunning: true,
        isPreparingDoc: false,
        isConnecting: false,
        progress: action.payload.steps?.length > 0 ? action.payload.steps : (state.progress.length > 0 ? state.progress : createInitialSteps()),
        progressPercent: 0
      };
    case 'MATTER_CREATION_STEP_STARTED':
      return {
        ...state,
        progress: merge_step(state.progress.length > 0 ? state.progress : createInitialSteps(), {
          step: action.payload.step,
          status: "In Progress",
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_STEP_MESSAGE':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "In Progress",
          messages: [action.payload.message],
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_STEP_COMPLETED':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "Completed",
          messages: [action.payload.message],
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_STEP_FAILED':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "Failed",
          messages: [action.payload.message],
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_COMPLETE':
      return {
        ...state,
        isPreparingDoc: false,
        isConnecting: false,
        correlationId: undefined,
        progress: [],
        isRunning: false,
        isCompleted: true
      };
    case 'MATTER_CREATION_FAILED':
    case 'MATTER_CREATION_CANCELLED':
      return {
        ...state,
        isPreparingDoc: false,
        isConnecting: false,
        correlationId: undefined,
        progress: [],
        isRunning: false,
        isCompleted: false
      };
    case 'SET_PREFETCHED_MATTER_DATA':
      return {
        ...state,
        prefetchedMatterData: action.payload.data,
        prefetchedMatterTasks: action.payload.tasks || [],
      };
    case 'SET_CURRENT_MATTER':
      return {
        ...state,
        currentMatter: action.payload,
      };
    case 'SET_LOADING_CURRENT_MATTER':
      return {
        ...state,
        isLoadingCurrentMatter: action.payload,
      };
    case 'CLEAR_PREFETCHED_MATTER_DATA':
      return {
        ...state,
        prefetchedMatterData: null,
        prefetchedMatterTasks: [],
      };
    case 'CLEAR_CURRENT_MATTER':
      return {
        ...state,
        currentMatter: null,
        isLoadingCurrentMatter: false,
      };
    default:
      return state;
  }
};
